/**
 * PipelineActivityRowView — one entry of the pipeline audit trail (SE-5504).
 *
 * Written for BOTH surfaces SE-5504 shipped, and now rendered by only one of
 * them: SE-6554 gave the whole-pipeline manager log its own month-grouped
 * timeline, leaving this row to the card-scoped trail inside the player
 * preview drawer (AC1/AC2). `showPlayerName` is the seam that kept the two
 * honest — a board-wide feed has to say WHICH player an entry concerns while a
 * card-scoped trail already has that in the header — and it is retained
 * because it costs nothing and is what a second consumer would need again.
 *
 * The restyle strips the row's card (border, fill, padding) and steps the type
 * down a rung: inside the drawer these entries are the last section of a long
 * scroll, and a stack of bordered cards there competes with the sections above
 * it that actually carry the player's data.
 *
 * ## The automated marker (AC2)
 *
 * An entry whose actor is not a human carries an `Automation` / `System` badge;
 * a human entry carries none, so the presence of the badge IS the distinction.
 *
 * The badge and the actor line answer different questions and are deliberately
 * not the same string (SE-6864): the badge names the KIND of actor
 * (`actorTypeLabel`) while the line beneath names WHICH actor
 * (`actorLabel` — the automation rule's own name, or the person's). A
 * rule-driven entry therefore reads `Automation` + "Fast-track U21 prospects",
 * which is what the attribution AC asks for without losing the AC2 marker.
 *
 * There was no pre-existing "auto" marker in this codebase to reuse — the
 * V1.6 auto-tag convention the AC refers to was never built (the tag chips
 * carry no provenance, and `KanbanTag` has no automation field). What DOES
 * exist is a consistent vocabulary for the same idea on the neighbouring
 * Review-queue surface, and this row reuses that rather than inventing a third
 * spelling:
 *   - the WORD is "Automation" (`SOURCE_LABEL` in `ReviewQueueRowContainer`,
 *     `SOURCE_FALLBACK_LABEL` in `reviewQueueRows`, and the Review-queue filter
 *     chip all spell it that way — never "Auto");
 *   - the SHAPE is `Badge` + `BadgeText`, the marker shape
 *     `AutomationRuleRowView` uses for its "Paused" state chip.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/PipelineActivityRow
 */

import { memo } from "react";
import { ZapIcon } from "lucide-react-native";

import { TEST_ID_PIPELINE_ACTIVITY } from "e2e/testId";

import { Badge, BadgeIcon, BadgeText } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { PipelineActivityRow } from "../../../utils/pipelineActivityRows";

/** Props for {@link PipelineActivityRowView}. */
export interface PipelineActivityRowViewProps {
  /** The row model. */
  readonly row: PipelineActivityRow;
  /**
   * Whether to print the player's name. The whole-pipeline log sets this; the
   * card-scoped trail leaves it off because the drawer header already names
   * the player.
   */
  readonly showPlayerName?: boolean;
}

/**
 * Render one audit-trail entry.
 * @param props - {@link PipelineActivityRowViewProps}
 * @param props.row - The row model
 * @param props.showPlayerName - Whether to print the player's name
 * @returns The row.
 */
const PipelineActivityRowView = ({
  row,
  showPlayerName = false,
}: PipelineActivityRowViewProps) => (
  <Box testID={TEST_ID_PIPELINE_ACTIVITY.rowFor(row.id)} className="gap-px">
    <Stack direction="horizontal" space="2" className="items-start">
      <Text
        variant="caption"
        className="flex-1 font-medium text-content-primary"
      >
        {row.description}
      </Text>
      {/* AC2 — automated entries, and ONLY automated entries, carry this. */}
      {row.isAutomated ? (
        <Badge
          action="info"
          variant="solid"
          testID={TEST_ID_PIPELINE_ACTIVITY.autoMarkerFor(row.id)}
        >
          <BadgeIcon as={ZapIcon} />
          <BadgeText>{row.actorTypeLabel}</BadgeText>
        </Badge>
      ) : null}
    </Stack>

    {showPlayerName && row.playerName ? (
      <Text variant="micro" className="text-content-secondary">
        {row.playerName}
      </Text>
    ) : null}

    <Stack direction="horizontal" space="1" className="items-center">
      <Text
        variant="micro"
        className="text-content-muted"
        testID={TEST_ID_PIPELINE_ACTIVITY.actorFor(row.id)}
      >
        {row.actorLabel}
      </Text>
      <Text variant="micro" className="text-content-muted">
        ·
      </Text>
      <Text
        variant="micro"
        className="text-content-muted"
        testID={TEST_ID_PIPELINE_ACTIVITY.timestampFor(row.id)}
      >
        {row.occurredAtLabel}
      </Text>
    </Stack>

    {/*
     * The reason typed into the sensitive-stage prompt (SE-5495 writes it onto
     * the MOVED entry). Rendering it here is what makes the trail an AUDIT
     * trail rather than a change list.
     */}
    {row.rationale ? (
      <Text variant="micro" className="text-content-secondary">
        {`“${row.rationale}”`}
      </Text>
    ) : null}
  </Box>
);

PipelineActivityRowView.displayName = "PipelineActivityRowView";

export default memo(PipelineActivityRowView);
